import { useErrorMessages } from '@AppModels/hooks/appStateHooks';
import { IModalContext, ModalChildComponentContextKey } from '@AppModels/index';
import { useEventLogging } from '@AppModels/Logging';
import { useContactRemoveTagsMutation } from '@Queries/Contact/useContactRemoveTagsMutation';
import { useGetSystemJobQuery } from '@Queries/SystemJob/useGetSystemJobQuery';
import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { asModalComponent } from '@WebComponents/Modal';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Pill } from '../../../components-v2/Pill/Pill';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import { styleSheet } from './styles';

interface Props extends IModalContext<string> {
	tagNames: string[];
	request?: Api.IBulkContactsRequest;
	totalContactCount?: number;
}

export function _ContactsRemoveTag({ request, tagNames, totalContactCount, parentModal }: Props) {
	const errorMessages = useErrorMessages();
	const { logInput, logApiError } = useEventLogging('ContactsRemoveTag');

	// State to track which tags are selected for removal
	const [selectedTags, setSelectedTags] = React.useState<string[]>(tagNames);

	// Update selected tags when tagNames prop changes
	React.useEffect(() => {
		setSelectedTags(tagNames);
	}, [tagNames]);

	const contactRemoveTagsMutation = useContactRemoveTagsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('ContactRemoveTagsMutation', error);
		},
	});

	const systemJobQuery = useGetSystemJobQuery({
		enabled: contactRemoveTagsMutation.isSuccess,
		jobId: contactRemoveTagsMutation.data?.id,
		refetchInterval: query => {
			const data = query.state.data;
			if (!data) {
				return false;
			}
			if (data.percentComplete < 100) {
				return 2000;
			}
			return false;
		},
	});

	const isSystemJobComplete =
		systemJobQuery.data?.percentComplete != null && systemJobQuery.data?.percentComplete >= 100;

	React.useEffect(() => {
		if (isSystemJobComplete) {
			parentModal?.onRequestClose(selectedTags.join(','), false);
		}
	}, [isSystemJobComplete, selectedTags, parentModal]);

	const onCloseClicked = () => {
		if (parentModal) {
			parentModal.onRequestClose(null, true);
		}
	};

	const toggleTagSelection = (tagName: string) => {
		setSelectedTags(prev => (prev.includes(tagName) ? prev.filter(t => t !== tagName) : [...prev, tagName]));
	};

	const onConfirmRemovalClicked = async () => {
		if (selectedTags.length === 0 || contactRemoveTagsMutation.isPending) {
			return;
		}

		logInput('Remove', 'Click', { tags: selectedTags });
		contactRemoveTagsMutation.mutateAsync({
			request: {
				...request,
				tags: selectedTags,
			},
		});
	};

	// Calculate the number of contacts affected
	const contactCount = React.useMemo(() => {
		// If totalContactCount is provided (when selecting all), use it
		if (totalContactCount !== undefined) {
			return totalContactCount;
		}
		// Otherwise use the includeContactIds length
		if (request?.includeContactIds && request.includeContactIds.length > 0) {
			return request.includeContactIds.length;
		}
		return 'selected';
	}, [request, totalContactCount]);

	return (
		<div className={`${css(styleSheet.container)} contacts-remove-tag`}>
			<div className={css(styleSheet.header)}>
				<DeprecatedCloseButton
					disabled={
						contactRemoveTagsMutation.isPending || (contactRemoveTagsMutation.isSuccess && !isSystemJobComplete)
					}
					onClick={onCloseClicked}
				/>
			</div>
			<div className={css(styleSheet.title)}>Remove Tag</div>
			{contactRemoveTagsMutation.isSuccess && !isSystemJobComplete ? (
				<div className={css(styleSheet.removingMessage)}>
					<div>Removing tag...</div>
					<LoadingSpinner type='small' />
				</div>
			) : (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.message)}>
						{tagNames.length === 1 ? (
							<>
								Remove <Pill variant='tag' label={tagNames[0]} color='blue' /> from{' '}
								{typeof contactCount === 'number' ? `${contactCount} contacts` : `${contactCount} contacts`}?
							</>
						) : (
							<>
								Select tags to remove from{' '}
								{typeof contactCount === 'number' ? `${contactCount} contacts` : `${contactCount} contacts`}:
							</>
						)}
					</div>
					{tagNames.length > 1 && (
						<div className={css(styleSheet.tagSelectionContainer)}>
							{tagNames.map(tagName => (
								<label key={tagName} className={css(styleSheet.tagCheckboxLabel)}>
									<input
										type='checkbox'
										checked={selectedTags.includes(tagName)}
										onChange={() => toggleTagSelection(tagName)}
										disabled={contactRemoveTagsMutation.isPending}
										className={css(styleSheet.tagCheckbox)}
									/>
									<Pill variant='tag' label={tagName} color='blue' />
								</label>
							))}
						</div>
					)}
					<div className={css(styleSheet.subMessage)}>
						This action cannot be undone. The contacts will remain but the {selectedTags.length === 1 ? 'tag' : 'tags'}{' '}
						will be removed.
					</div>
					<div className={css(styleSheet.buttonContainer)}>
						<button
							className={css(baseStyleSheet.ctaButtonReverse, styleSheet.cancelButton)}
							onClick={onCloseClicked}
							disabled={contactRemoveTagsMutation.isPending}
						>
							<span>Cancel</span>
						</button>
						<button
							className={css(baseStyleSheet.ctaButtonDestructiveSolid, styleSheet.confirmButton)}
							onClick={onConfirmRemovalClicked}
							disabled={contactRemoveTagsMutation.isPending || selectedTags.length === 0}
						>
							<span>{contactRemoveTagsMutation.isPending ? 'Removing...' : 'Confirm Removal'}</span>
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

const ContactsRemoveTagAsObserver = observer(_ContactsRemoveTag);
export const ContactsRemoveTag = inject(ModalChildComponentContextKey)(ContactsRemoveTagAsObserver);

export const ContactsRemoveTagModal = asModalComponent(ContactsRemoveTag, {
	className: 'contacts-remove-tag-modal',
	shouldCloseOnOverlayClick: false,
});
